import { apiClient } from '../api-client'
import { fileStorageApi } from './file-storage-api'

// Bugs API
export const bugsApi = {
  getBugs: (
    filters?: any,
    page = 1,
    limit = 20,
    paginated = false
  ): Promise<any> => {
    const safeLimit = Math.min(Math.max(limit, 1), 100)
    const safePage = Math.max(page, 1)
    const skip = (safePage - 1) * safeLimit
    const params = { ...(filters || {}), skip, limit: safeLimit, paginated } as any
    if (params.status && !params.status_filter) {
      params.status_filter = params.status
      delete params.status
    }
    return apiClient.get('/bugs', { params })
  },

  getBugAssignees: (filters?: {
    project_id?: string
    status?: string
    severity?: string
    search?: string
    overdue?: boolean
    recently_created?: boolean
    assigned_this_week?: boolean
    completed_this_week?: boolean
  }): Promise<Array<{ id: string; first_name?: string; last_name?: string; email?: string; name?: string }>> => {
    const params = { ...(filters || {}) } as any
    if (params.status && !params.status_filter) {
      params.status_filter = params.status
      delete params.status
    }
    return apiClient.get('/bugs/assignees', { params })
  },

  getBug: (id: string): Promise<any> =>
    apiClient.get(`/bugs/${id}`),

  createBug: (data: any): Promise<any> =>
    apiClient.post('/bugs', data),

  updateBug: (id: string, data: any): Promise<any> =>
    apiClient.patch(`/bugs/${id}`, data),

  assignBug: (id: string, assigneeId: string): Promise<any> =>
    apiClient.post(`/bugs/${id}/assign`, { assignee_id: assigneeId }),

  reopenBug: (id: string, reason: string): Promise<any> =>
    apiClient.post(`/bugs/${id}/reopen`, { reason }),

  pauseBug: (
    id: string,
    payload: { pause_type: 'client' | 'internal'; reason?: string; expected_resume_at?: string }
  ): Promise<any> =>
    apiClient.post(`/bugs/${id}/pause`, payload),

  resumeBug: (id: string): Promise<any> =>
    apiClient.post(`/bugs/${id}/resume`),

  getBugComments: (id: string): Promise<any[]> =>
    apiClient.get(`/bugs/${id}/comments`),

  addBugComment: (id: string, comment: any): Promise<any> =>
    apiClient.post(`/bugs/${id}/comments`, comment),

  getTaskBugs: (taskId: string): Promise<any[]> =>
    apiClient.get(`/bugs/task/${taskId}`),

  createTaskBug: (taskId: string, data: any): Promise<any> =>
    apiClient.post(`/bugs/task/${taskId}`, data),

  deleteBug: (id: string): Promise<void> =>
    apiClient.delete(`/bugs/${id}`),

  getBugWorkLogs: (bugId: string, userId?: string): Promise<any[]> =>
    apiClient.get(`/bugs/${bugId}/worklogs`, { params: { user_id: userId } }),

  getAttachments: (bugId: string): Promise<any[]> =>
    fileStorageApi.listAttachments('bug', bugId),

  uploadAttachment: async (bugId: string, file?: File, videoUrl?: string, projectId?: string): Promise<any> => {
    if (videoUrl) {
      const formData = new FormData()
      formData.append('video_url', videoUrl)
      return apiClient.post(`/bugs/${bugId}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
    } else if (file) {
      let resolvedProjectId = String(projectId || '').trim()
      if (!resolvedProjectId) {
        const bug = await bugsApi.getBug(bugId)
        resolvedProjectId = String(bug?.project_id || bug?.projectId || '').trim()
      }

      if (!resolvedProjectId) {
        throw new Error('Project ID is required to upload a bug attachment')
      }

      const uploadPayload = {
        project_id: resolvedProjectId,
        category: 'bugs' as const,
        entity_type: 'bug',
        entity_id: bugId,
      }

      return fileStorageApi.uploadEntityAttachment(uploadPayload, file)
    }
    throw new Error('Either file or videoUrl must be provided')
  },

  deleteAttachment: (_bugId: string, attachmentId: string): Promise<void> =>
    fileStorageApi.deleteAttachment(attachmentId),

  startBugTimer: (bugId: string, description?: string): Promise<any> =>
    apiClient.post(`/bugs/${bugId}/timer/start`, { description }),

  stopBugTimer: (bugId: string): Promise<any> =>
    apiClient.post(`/bugs/${bugId}/timer/stop`),

  getActiveBugTimer: (): Promise<any> =>
    apiClient.get('/bugs/timer/active'),

  getBugHistory: (bugId: string): Promise<any[]> =>
    apiClient.get(`/bugs/${bugId}/history`),

  getBugsDashboard: (filters?: any): Promise<any> =>
    apiClient.get('/bugs/dashboard', { params: filters }),

  getMyAssignedBugs: (filters?: any): Promise<any[]> =>
    apiClient.get('/bugs/my-assigned', { params: filters }),
}
